import { useParams, useNavigate } from "react-router-dom";
import { Navbar, Footer } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Send, ExternalLink, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { BackButton } from "@/components/BackButton";
import { useIsMobile } from "@/hooks/use-mobile";
import { filterProvinces } from "@/lib/filterData";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { PurchaseRequestDetailModal } from "@/components/PurchaseRequestDetailModal";
import { PurchaseAcceptedMessage, parseSystemMessage } from "@/components/PurchaseAcceptedMessage";
import { getDirectThreadPartner, isDirectThread } from "@/lib/threadIds";

interface Message {
  id: string;
  sender_id: string;
  content: string;
  created_at: string;
}

function getProvinceForMunicipality(municipality: string): string | null {
  for (const p of filterProvinces) {
    if (p.municipalities.includes(municipality)) return p.name;
  }
  return null;
}

function extractListingId(threadId: string): string | null {
  // thread format: listing-{listingId}-{userId}
  // UUID = 8-4-4-4-12 hex chars = 36 chars
  const match = threadId.match(/^listing-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})-/);
  return match ? match[1] : null;
}

function extractTrailingUserId(threadId: string): string | null {
  const match = threadId.match(/-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/);
  return match ? match[1] : null;
}

function PartnerSidebar({ partnerId }: { partnerId: string }) {
  const { data: profile } = useQuery({
    queryKey: ["partner-profile", partnerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("public_profiles")
        .select("id, username, full_name, avatar_url, location")
        .eq("id", partnerId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!partnerId,
  });

  if (!profile) return null;

  const initials = (profile.full_name || "?")
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const province = profile.location ? getProvinceForMunicipality(profile.location) : null;
  const locationText = profile.location
    ? province
      ? `${profile.location}, ${province}`
      : profile.location
    : null;

  return (
    <Link
      to={`/saljare/${(profile as any).username || profile.id}`}
      className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 transition-colors hover:bg-secondary"
    >
      <Avatar className="h-12 w-12 flex-shrink-0">
        {profile.avatar_url && <AvatarImage src={profile.avatar_url} alt={profile.full_name} />}
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold">{profile.full_name}</p>
        {locationText && (
          <p className="truncate text-xs text-muted-foreground">{locationText}</p>
        )}
      </div>
    </Link>
  );
}

function buildSpecs(listing: any): { label: string; value: string }[] {
  const specs: { label: string; value: string }[] = [];
  const listingType = listing.listing_type?.toLowerCase() ?? listing.category?.toLowerCase();

  if (listing.species) specs.push({ label: "Trädslag", value: listing.species });
  if (listing.wood_age) specs.push({ label: "Ålder", value: `${listing.wood_age} år` });

  if (listingType === "träd") {
    if (listing.diameter) specs.push({ label: "Diameter", value: `${listing.diameter} cm` });
    if (listing.quantity) specs.push({ label: "Antal", value: `${listing.quantity} träd` });
  } else if (listingType === "plankor" || listingType === "byggnadsvirke" || listingType === "snickerivirke") {
    if (listing.moisture_content) specs.push({ label: "Fuktkvot", value: listing.moisture_content });
    if (listing.processing_grade) specs.push({ label: "Bearbetningsgrad", value: listing.processing_grade });
    if (listing.quantity) specs.push({ label: "Antal", value: `${listing.quantity} plankor` });
  } else if (listingType === "ved") {
    if (listing.moisture_content) specs.push({ label: "Fuktkvot", value: listing.moisture_content });
    if (listing.quantity) specs.push({ label: "Mängd", value: `${listing.quantity} kubik` });
  } else {
    if (listing.diameter) specs.push({ label: "Diameter", value: `${listing.diameter} cm` });
    if (listing.moisture_content) specs.push({ label: "Fuktkvot", value: listing.moisture_content });
    if (listing.processing_grade) specs.push({ label: "Bearbetningsgrad", value: listing.processing_grade });
    if (listing.quantity) specs.push({ label: "Antal", value: `${listing.quantity}` });
  }

  const province = listing.municipality ? getProvinceForMunicipality(listing.municipality) : null;
  if (listing.municipality && province) {
    specs.push({ label: "Kommun & Län", value: `${listing.municipality}, ${province}` });
  } else if (listing.municipality) {
    specs.push({ label: "Kommun", value: listing.municipality });
  } else if (listing.location) {
    specs.push({ label: "Plats", value: listing.location });
  }

  return specs;
}

function ListingSidebar({ listingId }: { listingId: string }) {
  const { data: listing } = useQuery({
    queryKey: ["listing-sidebar", listingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("*")
        .eq("id", listingId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!listingId,
  });

  if (!listing) return null;

  const imageUrl = listing.images && listing.images.length > 0 ? listing.images[0] : null;
  const specs = buildSpecs(listing);

  return (
    <div className="flex flex-col gap-3">
      {/* Container 1 – Listing Preview */}
      <Link
        to={`/annonser/${listing.id}`}
        className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 transition-colors hover:bg-secondary"
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={listing.title}
            className="h-16 w-16 flex-shrink-0 rounded-md object-cover"
          />
        ) : (
          <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-md bg-secondary text-2xl">
            🌲
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-snug line-clamp-2">{listing.title}</p>
          {listing.price > 0 && (
            <p className="mt-1 text-sm font-semibold text-primary">
              {listing.price.toLocaleString("sv-SE")} kr
            </p>
          )}
        </div>
        <ExternalLink className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
      </Link>

      {/* Container 2 – Listing Specifications */}
      {specs.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 text-sm font-semibold">Specifikationer</h3>
          <div className="space-y-2">
            {specs.map((s, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{s.label}</span>
                  <span className="font-medium text-right">{s.value}</span>
                </div>
                {i < specs.length - 1 && <Separator className="mt-2" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface LinkedPurchaseRequest {
  id: string;
  listing_id: string;
  buyer_id: string;
  seller_id: string;
  quantity: number;
  total_price: number;
  delivery_method: string;
  pickup_date: string | null;
  pickup_time: string | null;
  shipping_address: string | null;
  status: string;
  proposed_pickup_date: string | null;
  proposed_pickup_time: string | null;
  created_at: string;
  listing_title?: string;
  listing_image?: string | null;
  listing_category?: string | null;
  pickup_address?: string | null;
  other_user_name?: string;
  other_user_email?: string;
  other_user_avatar?: string | null;
}

function statusBorder(status: string) {
  if (status === "accepted") return "border-primary";
  if (status === "denied") return "border-destructive";
  if (status === "completed") return "border-muted-foreground";
  return "border-border";
}

function getStatusBadge(r: LinkedPurchaseRequest, currentUserId: string) {
  if (r.status === "completed") return { label: "Slutförd", className: "bg-muted-foreground text-background" };
  if (r.status === "accepted") return { label: "Accepterad", className: "bg-primary text-primary-foreground" };
  if (r.status === "denied") return { label: "Nekad", className: "bg-destructive text-destructive-foreground" };
  if (r.status === "proposed") {
    const isBuyer = r.buyer_id === currentUserId;
    if (isBuyer) return { label: "Väntar ditt svar", className: "bg-muted-foreground text-background" };
    return { label: "Förslag skickat", className: "bg-muted-foreground text-background" };
  }
  return null;
}

function LinkedPurchaseRequestCard({
  listingId,
  buyerId,
  currentUserId,
}: {
  listingId: string;
  buyerId: string;
  currentUserId: string;
}) {
  const [request, setRequest] = useState<LinkedPurchaseRequest | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchRequest = async () => {
    const { data } = await supabase
      .from("purchase_requests")
      .select("*")
      .eq("listing_id", listingId)
      .eq("buyer_id", buyerId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!data) {
      setRequest(null);
      return;
    }

    const otherUserId = data.buyer_id === currentUserId ? data.seller_id : data.buyer_id;
    const [{ data: listing }, { data: profile }] = await Promise.all([
      supabase.from("listings").select("title, images, category, pickup_address").eq("id", data.listing_id).maybeSingle(),
      supabase.from("public_profiles").select("full_name, avatar_url").eq("id", otherUserId).maybeSingle(),
    ]);

    setRequest({
      ...data,
      listing_title: listing?.title || "Annons",
      listing_image: listing?.images?.[0] || null,
      listing_category: listing?.category || null,
      pickup_address: listing?.pickup_address || null,
      other_user_name: profile?.full_name || "Okänd",
      other_user_email: profile?.full_name
        ? profile.full_name.toLowerCase().replace(/\s+/g, ".") + "@email.se"
        : "",
      other_user_avatar: profile?.avatar_url || null,
    });
  };

  useEffect(() => {
    fetchRequest();
  }, [listingId, buyerId, currentUserId]);

  if (!request) return null;

  const isSent = request.buyer_id === currentUserId;
  const statusBadge = getStatusBadge(request, currentUserId);

  const deliveryLabel = (m: string) => {
    if (m === "pickup" || m === "hämta") return "Hämta";
    if (m === "shipping" || m === "leverans") return "Leverans";
    return m;
  };

  const effectivePickupDate = request.proposed_pickup_date ?? request.pickup_date;
  const effectivePickupTime = request.proposed_pickup_time ?? request.pickup_time;
  const isShipping = request.delivery_method === "shipping" || request.delivery_method === "leverans";

  return (
    <>
      <Separator className="my-3" />
      <button
        onClick={() => setDetailOpen(true)}
        className={cn(
          "relative w-full rounded-lg border-2 bg-card p-4 text-left transition-shadow hover:shadow-md",
          statusBorder(request.status)
        )}
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-muted-foreground">
            {isSent ? "Köpförslag skickat" : "Köpförslag mottaget"}
          </p>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {statusBadge && (
              <Badge className={cn("text-[10px] border-0", statusBadge.className)}>
                {statusBadge.label}
              </Badge>
            )}
          </div>
        </div>

        <div className="mb-3 flex items-baseline justify-between">
          <span className="text-lg font-bold text-heading">
            {request.total_price.toLocaleString("sv-SE")} kr
          </span>
          <span className="text-sm text-muted-foreground">x{request.quantity}</span>
        </div>

        <div className="space-y-1 text-xs text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">Leverans: </span>
            {deliveryLabel(request.delivery_method)}
          </p>
          {isShipping ? (
            request.shipping_address && (
              <p>
                <span className="font-medium text-foreground">Adress: </span>
                {request.shipping_address}
              </p>
            )
          ) : (
            effectivePickupDate && (
              <p>
                <span className="font-medium text-foreground">Hämtas: </span>
                {new Date(effectivePickupDate).toLocaleDateString("sv-SE", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
                {effectivePickupTime && ` kl. ${effectivePickupTime}`}
              </p>
            )
          )}
        </div>
      </button>

      <PurchaseRequestDetailModal
        open={detailOpen}
        onOpenChange={setDetailOpen}
        request={request as any}
        currentUserId={currentUserId}
        onUpdated={fetchRequest}
      />
    </>
  );
}

export default function MessageThread() {
  const { threadId } = useParams<{ threadId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const isDirect = threadId ? isDirectThread(threadId) : false;
  const directPartnerId = threadId && user?.id ? getDirectThreadPartner(threadId, user.id) : null;
  const listingId = !isDirect && threadId ? extractListingId(threadId) : null;
  const trailingUserId = !isDirect && threadId ? extractTrailingUserId(threadId) : null;

  const { data: listingMeta } = useQuery({
    queryKey: ["listing-seller", listingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("seller_id")
        .eq("id", listingId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!listingId,
  });

  // Partner: prefer a sender_id from messages that isn't current user; else use seller_id (if not us); else trailing id; else direct partner
  const partnerFromMessages = messages.find((m) => m.sender_id !== user?.id)?.sender_id ?? null;
  const partnerId =
    partnerFromMessages ??
    (listingMeta?.seller_id && listingMeta.seller_id !== user?.id ? listingMeta.seller_id : null) ??
    (trailingUserId && trailingUserId !== user?.id ? trailingUserId : null) ??
    directPartnerId;

  useEffect(() => {
    if (!threadId) return;
    
    supabase
      .from("messages")
      .select("id, sender_id, content, created_at")
      .eq("thread_id", threadId)
      .order("created_at", { ascending: true })
      .then(({ data }) => { if (data) setMessages(data as Message[]); });

    const channel = supabase
      .channel(`thread-${threadId}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "messages",
        filter: `thread_id=eq.${threadId}`,
      }, (payload) => {
        setMessages(prev => [...prev, payload.new as Message]);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [threadId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim() || !user || !threadId) return;
    setSending(true);
    await supabase.from("messages").insert({
      thread_id: threadId,
      sender_id: user.id,
      content: newMsg.trim(),
    });
    setNewMsg("");
    setSending(false);
  };

  const handleDeleteChat = async () => {
    if (!threadId) return;
    setDeleting(true);
    const { error } = await supabase.rpc("soft_delete_thread", { _thread_id: threadId });
    setDeleting(false);
    if (error) {
      toast.error("Kunde inte ta bort chatten");
    } else {
      toast.success("Chatten har tagits bort");
      navigate("/dashboard");
    }
  };

  return (
    <div className="flex h-screen md:h-auto md:min-h-screen flex-col overflow-hidden md:overflow-visible">
      <Navbar />
      <div className="container flex flex-1 gap-6 py-2 md:py-4 min-h-0">
        {/* Chat column */}
        <div className="flex flex-1 flex-col min-w-0 min-h-0">
          <BackButton fallback="/dashboard" label="Tillbaka" className="mb-2 md:mb-4 text-muted-foreground" />
          
          <div className="flex-1 min-h-0 overflow-y-auto rounded-lg border border-border bg-card p-4">
            <div className="space-y-3">
              {messages.map((m) => {
                const systemPayload = parseSystemMessage(m.content);
                if (systemPayload) {
                  return (
                    <PurchaseAcceptedMessage
                      key={m.id}
                      payload={systemPayload}
                      timestamp={m.created_at}
                    />
                  );
                }
                return (
                  <div
                    key={m.id}
                    className={`flex ${m.sender_id === user?.id ? "justify-end" : "justify-start"}`}
                  >
                    <div className={`max-w-[75%] rounded-lg px-4 py-2 ${
                      m.sender_id === user?.id
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-secondary-foreground"
                    }`}>
                      <p className="text-sm">{m.content}</p>
                      <p className="mt-1 text-[10px] opacity-70">
                        {new Date(m.created_at).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
          </div>

          <div className="mt-3 flex gap-2">
            <Input
              value={newMsg}
              onChange={(e) => setNewMsg(e.target.value)}
              placeholder="Skriv ett meddelande…"
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />
            <Button onClick={handleSend} disabled={sending || !newMsg.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Right sidebar – listing/partner context (hidden on mobile) */}
        {!isMobile && (listingId || partnerId) && (
          <aside className="mt-[52px] w-72 flex-shrink-0 lg:w-80">
            {partnerId && (
              <>
                <PartnerSidebar partnerId={partnerId} />
                {listingId && <Separator className="my-3" />}
              </>
            )}
            {listingId && <ListingSidebar listingId={listingId} />}
            {listingId && trailingUserId && user?.id && (
              <LinkedPurchaseRequestCard
                listingId={listingId}
                buyerId={trailingUserId}
                currentUserId={user.id}
              />
            )}
            <Button
              variant="outline"
              className="mt-3 w-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Ta bort chatt
            </Button>
          </aside>
        )}
      </div>
      <Footer hideOnMobile />

      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Är du säker?</DialogTitle>
            <DialogDescription>
              Alla meddelanden i den här chatten kommer att tas bort permanent. Detta går inte att ångra.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Avbryt</Button>
            <Button
              variant="destructive"
              onClick={handleDeleteChat}
              disabled={deleting}
            >
              {deleting ? "Tar bort…" : "Ta bort chatt"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
